import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { formatMonth } from '../lib/format'

interface Props {
  selected: string                // YYYY-MM-DD
  onSelect: (iso: string) => void
  daysWithEvents?: Set<string>    // ISO dates that should show a dot
  onMonthChange?: (year: number, month0: number) => void  // month0 = 0..11
}

const DOW = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс']

const isoOf = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

function parseIso(iso: string): Date {
  return new Date(iso + 'T00:00:00')
}

export default function MonthGrid({ selected, onSelect, daysWithEvents, onMonthChange }: Props) {
  const initial = selected ? parseIso(selected) : new Date()
  const [view, setView] = useState({ y: initial.getFullYear(), m: initial.getMonth() })

  // Если selected меняется извне на другой месяц — подвинуть отображение
  useEffect(() => {
    if (!selected) return
    const d = parseIso(selected)
    if (d.getFullYear() !== view.y || d.getMonth() !== view.m) {
      setView({ y: d.getFullYear(), m: d.getMonth() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  const shift = (delta: number) => {
    const next = new Date(view.y, view.m + delta, 1)
    const ny = next.getFullYear()
    const nm = next.getMonth()
    setView({ y: ny, m: nm })
    onMonthChange?.(ny, nm)
  }

  const firstDay = new Date(view.y, view.m, 1)
  const lastDay = new Date(view.y, view.m + 1, 0)
  const weekStart = (firstDay.getDay() + 6) % 7
  const cells: (Date | null)[] = []
  for (let i = 0; i < weekStart; i++) cells.push(null)
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(new Date(view.y, view.m, d))

  const todayIso = isoOf(new Date())

  return (
    <div className="month-grid-wrap">
      <div className="month-title-row">
        <button className="month-nav" onClick={() => shift(-1)} aria-label="Предыдущий месяц">
          <ChevronLeft />
        </button>
        <div className="month-title">{formatMonth(firstDay)} {view.y}</div>
        <button className="month-nav" onClick={() => shift(1)} aria-label="Следующий месяц">
          <ChevronRight />
        </button>
      </div>
      <div className="month-grid">
        {DOW.map((d) => <div key={d} className="month-dow">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />
          const iso = isoOf(d)
          const isSel = iso === selected
          const isToday = iso === todayIso
          const has = daysWithEvents?.has(iso)
          const cls = [
            'month-day',
            isToday && 'is-today',
            isSel && 'is-selected',
            has && 'has-events',
          ].filter(Boolean).join(' ')
          return (
            <button key={i} className={cls} onClick={() => onSelect(iso)}>
              {d.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}
